<script setup>
import { X, HelpCircle } from 'lucide-vue-next'
import EnquiryForm from '@/Components/Support/EnquiryForm.vue'

/**
 * The help panel does not lock the page.
 *
 * It used to, because it was built from the same parts as the dialogs. But it
 * is not a dialog: it is a small panel in the corner, it covers nothing the
 * reader was looking at, and there is no decision it is waiting on. Locking the
 * page meant somebody who opened it to ask about the thing they were reading
 * could no longer scroll to the thing they were reading.
 *
 * On a phone there is no corner to spare, so it rises from the bottom across
 * the full width, over a dimmed page, as phones present a sheet.
 */
defineProps({
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['close'])

const close = () => emit('close')
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="open"
        role="dialog"
        aria-labelledby="support-panel-title"
        class="fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] flex-col overflow-hidden rounded-t-2xl border border-line bg-surface shadow-2xl sm:inset-x-auto sm:bottom-6 sm:left-6 sm:w-96 sm:max-w-[calc(100dvw-3rem)] sm:rounded-2xl"
      >
        <!-- Header -->
        <div class="flex shrink-0 items-center justify-between bg-gradient-to-r from-teal-500 to-teal-800 px-5 py-4">
          <div class="flex items-center gap-3">
            <div class="flex size-9 items-center justify-center rounded-lg bg-white/20">
              <HelpCircle class="size-5 text-white" />
            </div>
            <div>
              <h3 id="support-panel-title" class="text-sm font-semibold text-white">Help & Support</h3>
              <p class="text-xs text-white/70">We typically reply within 24 hours</p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            class="rounded-lg p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            @click="close"
          >
            <X class="size-5" />
          </button>
        </div>

        <!-- Body -->
        <div class="overflow-y-auto p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <EnquiryForm id-prefix="support" @close="close" />
        </div>
      </div>
    </Transition>

    <!-- Backdrop (click to close): dimmed on a phone, where the sheet covers the page -->
    <Transition
      enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-40 bg-ink/40 sm:bg-transparent"
        @click="close"
      />
    </Transition>
  </Teleport>
</template>
